import { Link } from "react-router-dom";
import { SignInButton, SignedIn, SignedOut } from "@clerk/clerk-react";
import {
  ArrowRight,
  Sparkles,
  Share2,
  Users,
  PenLine,
  RefreshCw,
  FileCheck2,
  ShieldCheck,
  Lock,
  BookOpen,
  CheckCircle2,
  Settings,
  FileText,
  Play,
  MapPin,
  Brain,
  Laptop,
} from "lucide-react";
import { Section } from "@/layouts/Section";
import { DemoCard } from "@/components/marketing/DemoCard";
const PLANS = [
  {
    name: "Free",
    price: "$0",
    period: "forever",
    teacherFeatures: [
      "20 report credits on signup",
      "20 AI generation credits on signup",
      "All report card types (Gr 1–12)",
      "PDF download",
      "Class sharing with view or edit permissions",
      "Bulk export to ZIP",
      "Save report defaults per class",
    ],
    adminFeatures: [],
  },
  {
    name: "School",
    price: "CA$24.99",
    period: "/ teacher / year",
    teacherFeatures: [
      "120 report credits per teacher / year",
      "200 AI generation credits per teacher / year",
      "All report card types (Gr 1–12)",
      "PDF download",
      "Class sharing with view or edit permissions",
      "Bulk export to ZIP",
      "Save report defaults per class",
    ],
    adminFeatures: [
      "School admin dashboard",
      "Manage and remove teachers",
      "Assign and manage seats",
      "View all classes across the school",
      "Reassign class ownership between teachers",
      "Per-teacher usage and activity stats",
      "Billing portal + seat management",
      "Controlled sharing mode with approval workflow",
    ],
  },
];

const BENEFITS = [
  {
    icon: Share2,
    title: "Class sharing with permissions",
    text: "Share classes with colleagues - give them view or edit permissions",
    status: "ready",
  },
  {
    icon: RefreshCw,
    title: "Bulk field updates",
    text: "Update a single field and apply it instantly to the entire class",
    status: "ready",
  },
  {
    icon: FileCheck2,
    title: "Perfectly formatted PDFs",
    text: "No more adjusting the text box and font size - PDF output is always formatted perfectly",
    status: "ready",
  },
  {
    icon: Sparkles,
    title: "AI comment generation",
    text: "AI generation to quickly write personalized student comments",
    status: "beta",
  },
  {
    icon: Users,
    title: "Simultaneous Live Editing",
    text: "Multiple teachers work on one report with no conflicts. You can focus on your Science Comment while the English Teacher focuses on their comment",
    status: "comingSoon",
  },
  {
    icon: PenLine,
    title: "Digital signatures",
    text: "Sign reports digitally - no printing just to sign and scan",
    status: "comingSoon",
  },
  {
    icon: ShieldCheck,
    title: "AI proofreading",
    text: "Scan your whole class of reports for typos and silly mistakes",
    status: "comingSoon",
  },
];

const TRUST_ITEMS = [
  {
    icon: BookOpen,
    title: "PIPEDA Compliant",
    desc: "We follow Canada's Personal Information Protection and Electronic Documents Act. Your student data is handled with the privacy obligations Ontario teachers are bound by.",
  },
  {
    icon: Lock,
    title: "Encrypted in transit and at rest",
    desc: "All data is encrypted over TLS in transit and encrypted at rest in our database. Your students' information never leaves without protection.",
  },
  {
    icon: FileText,
    title: "Ontario curriculum-aligned",
    desc: "Built specifically to the Ontario Ministry of Education's report card formats - not adapted from a generic template.",
  },
  {
    icon: MapPin,
    title: "Student data never leaves Canada",
    desc: "All AI processing runs on Google Cloud's Montréal region (northamerica-northeast1). Student information is never routed through servers outside Canada.",
  },
  {
    icon: Brain,
    title: "AI that never trains on your students",
    desc: "EasyReports uses Google Cloud's Vertex AI under enterprise terms. Your student data is never used to train or improve any AI model - by us or by Google.",
  },
  {
    icon: Laptop,
    title: "PDFs generated on your device",
    desc: "Report card PDFs are assembled entirely in your browser. Student names and grades are never sent to a server for PDF creation - the file is built and downloaded locally.",
  },
];

const SCHOOL_FEATURES = [
  {
    icon: Settings,
    title: "Admin controls for your whole school",
    desc: "School admins get a dedicated panel to manage teachers, assign seats, view all classes, and oversee the reporting cycle across your staff.",
  },
  {
    icon: PenLine,
    title: "Signature and logo customization",
    desc: "Place your school logo and administrator signature on reports easily. Every PDF looks like it came from your school.",
    comingSoon: true,
  },
  {
    icon: FileCheck2,
    title: "Always the latest government versions",
    desc: "We maintain up-to-date templates matching the current Ontario Ministry of Education report card formats so you never have to worry about version changes.",
  },
];

export default function Home() {
  return (
    <div className="w-full">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="bg-background px-8 py-14 pb-16 overflow-hidden">
        <div className="max-w-[1100px] mx-auto grid grid-cols-2 gap-16 items-center max-[860px]:grid-cols-1 max-[860px]:gap-12">
          <div className="flex flex-col gap-5">
            <span className="inline-block text-[0.8125rem] font-semibold tracking-[0.08em] uppercase text-brand-600 px-3 py-[0.3rem] bg-brand-600/10 rounded-full w-fit">
              For Ontario teachers
            </span>
            <h1 className="font-display text-[clamp(2.25rem,4vw,3.25rem)] font-normal leading-[1.15] text-foreground m-0">
              Making Report Cards<br />
              should be <em className="not-italic text-brand-700">EASY.</em>
            </h1>
            <p className="text-[1.0625rem] leading-[1.65] text-muted-foreground max-w-[480px] m-0">
              So we made a solution that cuts through all the stressful parts.
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              <SignedOut>
                <SignInButton mode="modal" afterSignInUrl="/dashboard" afterSignUpUrl="/dashboard">
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-brand-800 text-white text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-brand-700 hover:-translate-y-px no-underline whitespace-nowrap">
                    Make a Free Account
                    <ArrowRight size={16} />
                  </button>
                </SignInButton>
              </SignedOut>
              <SignedIn>
                <Link to="/dashboard">
                  <button className="inline-flex items-center gap-2 px-6 py-3 bg-brand-800 text-white text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-brand-700 hover:-translate-y-px no-underline whitespace-nowrap">
                    Go to Dashboard
                    <ArrowRight size={16} />
                  </button>
                </Link>
              </SignedIn>
            </div>
            <p className="text-[0.8rem] text-muted-foreground m-0">
              No credit card required &nbsp;·&nbsp; Free tier always available
            </p>
          </div>

          <DemoCard />
        </div>
      </section>

      {/* ── BENEFITS ─────────────────────────────────────── */}
      <Section variant="muted">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">What we solve</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-12 leading-tight">We cut through all the problems.</h2>
          <ul className="list-none p-0 m-0 grid grid-cols-3 gap-5 max-[860px]:grid-cols-2 max-[560px]:grid-cols-1">
            {BENEFITS.map((b) => (
              <li key={b.text} className="flex flex-col gap-3 p-6 bg-card border border-border rounded-[14px] relative">
                <div className="w-10 h-10 rounded-[10px] bg-brand-700/10 text-brand-600 dark:bg-brand-400/15 dark:text-brand-300 flex items-center justify-center">
                  <b.icon size={18} />
                </div>
                <h3 className="text-[1.0rem] font-semibold text-foreground m-0">{b.title}</h3>
                <span className="text-[0.9rem] text-muted-foreground leading-[1.5]">{b.text}</span>
                {b.status === "beta" && (
                  <span className="absolute top-4 right-4 text-[0.575rem] font-bold tracking-[0.07em] uppercase px-[0.55rem] py-[0.18rem] rounded-full bg-brand-600/10 text-brand-600 dark:bg-brand-400/15 dark:text-brand-300 border border-brand-600/20 whitespace-nowrap">
                    Beta
                  </span>
                )}
                {b.status === "comingSoon" && (
                  <span className="absolute top-4 right-4 text-[0.575rem] font-bold tracking-[0.07em] uppercase px-[0.55rem] py-[0.18rem] rounded-full bg-muted text-muted-foreground border border-border whitespace-nowrap">
                    Coming soon
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* ── LIVE DEMO CTA ────────────────────────────────── */}
      <Section variant="dark">
        <div className="max-w-[1100px] mx-auto px-8 grid grid-cols-2 gap-16 items-center max-[860px]:grid-cols-1 max-[860px]:gap-12 max-[860px]:px-6">
          <div className="flex flex-col gap-5">
            <div className="text-xs font-bold tracking-[0.1em] uppercase text-brand-300 mb-3">Try it yourself</div>
            <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-white m-0 mb-12 leading-tight">
              Watch the report being generated as you make live edits.
            </h2>
            <p className="text-[1.0625rem] leading-[1.65] text-white/65 m-0">
              No account. No credit card. Just open a report and start filling
              in a student - we generate it for you in real time.
            </p>
            <Link to="/free-trial">
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-primary-hover hover:-translate-y-px no-underline whitespace-nowrap w-fit">
                <Play size={15} />
                Try the live demo - no account needed
              </button>
            </Link>
          </div>
          <div className="max-[860px]:hidden">
            <DemoCard />
          </div>
        </div>
      </Section>

      {/* ── TRUST ────────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">Built to be trusted</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-12 leading-tight">Made for Teachers, by Teachers.</h2>
          <p className="text-[1.0625rem] leading-[1.7] text-muted-foreground max-w-[600px] m-0 mb-7">
            EasyReports was built by an Ontario teacher who lived through the
            report card grind. That means it is designed around real classroom
            constraints - not a startup&apos;s idea of what teachers need.
          </p>
          <div className="grid grid-cols-3 gap-6 max-[720px]:grid-cols-1">
            {TRUST_ITEMS.map((item) => (
              <div key={item.title} className="bg-card border border-border rounded-[14px] p-7">
                <div className="w-10 h-10 rounded-[10px] bg-brand-700/10 text-brand-600 dark:bg-brand-400/15 dark:text-brand-300 flex items-center justify-center mb-4">
                  <item.icon size={20} />
                </div>
                <h3 className="text-[1.0625rem] font-semibold text-foreground m-0 mb-2">{item.title}</h3>
                <p className="text-[0.9rem] leading-relaxed text-muted-foreground m-0">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>


      {/* ── PRICING ──────────────────────────────────────── */}
      <Section variant="dark">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-brand-300 mb-3">Pricing</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-white m-0 mb-12 leading-tight">
            Start free. Upgrade when your Colleagues are ready to join you.
          </h2>
          <div className="grid grid-cols-2 gap-5 max-w-[800px] mx-auto max-[640px]:grid-cols-1">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                className="bg-white/[0.06] border border-white/[0.12] rounded-[14px] p-7"
              >
                <div className="text-sm font-semibold tracking-[0.04em] uppercase text-white/60 mb-2">{plan.name}</div>
                <div className="font-display text-[2.25rem] text-white leading-none mb-5">
                  {plan.price}
                  <span className="font-sans text-sm text-white/50 ml-[2px]">{plan.period}</span>
                </div>
                <div className="text-[0.6rem] font-bold tracking-[0.1em] uppercase text-white/35 mb-2">For Teachers</div>
                <ul className="list-none p-0 m-0 flex flex-col gap-[0.6rem]">
                  {plan.teacherFeatures.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-white/75">
                      <CheckCircle2 size={13} className="text-[hsl(152,55%,55%)] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                {plan.adminFeatures.length > 0 && (
                  <>
                    <div className="text-[0.6rem] font-bold tracking-[0.1em] uppercase text-white/35 mt-5 mb-2">For Admins</div>
                    <ul className="list-none p-0 m-0 flex flex-col gap-[0.6rem]">
                      {plan.adminFeatures.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-sm text-white/75">
                          <CheckCircle2 size={13} className="text-[hsl(152,55%,55%)] shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/pricing">
              <button className="inline-flex items-center gap-[0.4rem] px-5 py-[0.72rem] bg-transparent text-white/85 text-[0.9375rem] font-medium border border-white/30 rounded-lg cursor-pointer transition-[border-color,background,transform] duration-150 hover:border-white hover:-translate-y-px hover:text-white hover:bg-white/[0.06] no-underline whitespace-nowrap">
                See full pricing details <ArrowRight size={14} />
              </button>
            </Link>
          </div>
        </div>
      </Section>    


      {/* ── SCHOOL & BOARD HOOK ───────────────────────────── */}
      <Section variant="muted">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">For schools &amp; boards</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-12 leading-tight">Built for the whole school, not just one teacher.</h2>
          <div className="grid grid-cols-3 gap-6 max-[720px]:grid-cols-1">
            {SCHOOL_FEATURES.map((f) => (
              <div key={f.title} className="bg-card border border-border rounded-[14px] p-7 relative">
                {f.comingSoon && (
                  <span className="absolute top-4 right-4 text-[0.575rem] font-bold tracking-[0.07em] uppercase px-[0.55rem] py-[0.18rem] rounded-full bg-muted text-muted-foreground border border-border whitespace-nowrap">
                    Coming soon
                  </span>
                )}
                <div className="w-10 h-10 rounded-[10px] bg-brand-700/10 text-brand-600 dark:bg-brand-400/15 dark:text-brand-300 flex items-center justify-center mb-4">
                  <f.icon size={20} />
                </div>
                <h3 className="text-[1.0625rem] font-semibold text-foreground m-0 mb-2">{f.title}</h3>
                <p className="text-[0.9rem] leading-relaxed text-muted-foreground m-0">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

    </div>
  );
}
